<template>
    <section class="addon-section">
        <header class="section-header">
            <h1 class="section-title">Dodatki dla modeli</h1>
            <button class="btn btn-submit" @click="openForm()">
                <font-awesome-icon icon="plus" /> Dodaj
            </button>
        </header>

        <EntityFilterBar v-model:search="searchQuery" v-model:sort="sortModel" v-model:filters="filters"
            :filters-schema="[
                { key: 'addon_desc', label: 'Dodatek', type: 'select', options: addonOptions },
                { key: 'trailer_name', label: 'Model', type: 'select', options: trailerOptions }
            ]" :sort-options="[
                { key: 'price', label: 'Cena' },
                { key: 'is_default', label: 'Domyślny' }
            ]" />

        <PaginationControls :current-page="currentPage" :total-pages="totalPages" @update:page="goToPage" />

        <div class="status-wrapper">
            <p v-if="addonForModelStore.loading" class="status-loading">Ładowanie...</p>
            <p v-if="addonForModelStore.error" class="status-error">Błąd: {{ addonForModelStore.error.message ||
                addonForModelStore.error }}</p>
        </div>

        <div v-if="!addonForModelStore.loading">
            <div v-if="filteredAddonsForModels.length" class="addon-list">
                <EntityCard v-for="item in paginatedAddonsForModels" :key="item.id">
                    <template #content>
                        <div class="addon-content">
                            <div class="addon-details">
                                <div class="detail-item">
                                    <span class="detail-label">Dodatek:</span>
                                    <span>{{ this.trailerStore.getAddonNameById(item.addon_id) }}</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Model:</span>
                                    <span>{{ this.trailerStore.getTrailerNameById(item.trailer_id) }}</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Cena:</span>
                                    <span>
                                        {{ item.price && !isNaN(parseFloat(item.price))
                                            ? parseFloat(item.price).toFixed(2) + ' zł'
                                            : 'brak ceny' }}
                                    </span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Domyślny:</span>
                                    <span>{{ item.is_default ? 'Tak' : 'Nie' }}</span>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #actions>
                        <button class="btn btn-edit" @click="editItem(item)">
                            <font-awesome-icon icon="pen" />
                            Edytuj
                        </button>
                        <button class="btn btn-delete" @click="confirmDelete(item.id)">
                            <font-awesome-icon icon="trash" />
                            Usuń
                        </button>
                    </template>
                </EntityCard>
            </div>

            <div v-else class="empty-state">
                <font-awesome-icon icon="plus-square" size="2x" />
                <p>Brak dodatków dla modeli do wyświetlenia</p>
            </div>
        </div>

        <PaginationControls :current-page="currentPage" :total-pages="totalPages" @update:page="goToPage" />

        <teleport to="body">
            <transition name="fade">
                <AddonForModelForm v-if="showForm" :visible="showForm" :addon="editingItem"
                    :addons="addonForModelStore.addons" :trailers="addonForModelStore.trailers"
                    @close="showForm = false" @save="handleSave" />

            </transition>
        </teleport>

        <teleport to="body">
            <ConfirmDialog :visible="confirmDialog.state.isVisible" :title="confirmDialog.state.dialogOptions.title"
                :message="confirmDialog.state.dialogOptions.message"
                :confirmText="confirmDialog.state.dialogOptions.confirmText"
                :cancelText="confirmDialog.state.dialogOptions.cancelText"
                :danger="confirmDialog.state.dialogOptions.danger" @confirm="confirmDialog.handleConfirm"
                @cancel="confirmDialog.handleCancel" @close="confirmDialog.handleClose" />
        </teleport>
    </section>
</template>

<script>
import { useAddonForModelStore } from '@/stores/addonForModelStore'
import { useAddonStore } from '@/stores/addonStore'
import { useTrailerStore } from '@/stores/trailerStore'
import { showErrorToast, showSuccessToast } from '@/utils/errorUtils'
import { useConfirmDialog } from '@/composables/useConfirmDialog'
import EntityCard from '@/components/cms/EntityCard.vue'
import EntityFilterBar from '@/components/cms/EntityFilterBar.vue'
import AddonForModelForm from './AddonForModelForm.vue'
import PaginationControls from '@/components/cms/PaginationControls.vue'
import ConfirmDialog from '@/components/cms/ConfirmDialog.vue'

export default {
    components: { EntityCard, EntityFilterBar, AddonForModelForm, PaginationControls, ConfirmDialog },
    setup() {
        const confirmDialog = useConfirmDialog()
        return { confirmDialog }
    },
    data() {
        return {
            addonForModelStore: useAddonForModelStore(),
            addonStore: useAddonStore(),
            trailerStore: useTrailerStore(),
            searchQuery: '',
            sortModel: {
                by: '',
                order: 'asc',
            },
            filters: {
                addon_desc: '',
                trailer_name: '',
            },
            showForm: false,
            editingItem: null,

            currentPage: 1,
            itemsPerPage: 100,
        }
    },
    computed: {
        filteredAddonsForModels() {
            let filtered = this.addonForModelStore.addonsForModels

            if (this.searchQuery) {
                const q = this.searchQuery.toLowerCase()
                filtered = filtered.filter(item =>
                    this.trailerStore.getAddonNameById(item.addon_id).toLowerCase().includes(q) ||
                    this.trailerStore.getTrailerNameById(item.trailer_id).toLowerCase().includes(q)
                )
            }

            if (this.filters.addon_desc) {
                filtered = filtered.filter(item =>
                    this.trailerStore.getAddonNameById(item.addon_id) === this.filters.addon_desc
                )
            }

            if (this.filters.trailer_name) {
                filtered = filtered.filter(item =>
                    this.trailerStore.getTrailerNameById(item.trailer_id) === this.filters.trailer_name
                )
            }

            if (this.sortModel.by) {
                filtered = [...filtered].sort((a, b) => {
                    const aVal = this.sortModel.by === 'price' ? a.price : a.is_default ? 1 : 0
                    const bVal = this.sortModel.by === 'price' ? b.price : b.is_default ? 1 : 0
                    if (aVal < bVal) return this.sortModel.order === 'asc' ? -1 : 1
                    if (aVal > bVal) return this.sortModel.order === 'asc' ? 1 : -1
                    return 0
                })
            }

            return filtered
        },

        paginatedAddonsForModels() {
            const start = (this.currentPage - 1) * this.itemsPerPage
            return this.filteredAddonsForModels.slice(start, start + this.itemsPerPage)
        },

        totalPages() {
            return Math.ceil(this.filteredAddonsForModels.length / this.itemsPerPage)
        },

        addonOptions() {
            return this.addonStore.addons.map(addon => {
                const name = this.trailerStore.getAddonNameById(addon.id)
                return { id: name, name }
            })
        },

        trailerOptions() {
            return this.trailerStore.trailers.map(trailer => {
                const name = this.trailerStore.getTrailerNameById(trailer.id)
                return { id: name, name }
            })
        },
    },
    methods: {
        openForm(item = null) {
            this.editingItem = item
            this.showForm = true
        },
        editItem(item) {
            this.openForm(item)
        },
        async confirmDelete(id) {
            const confirmed = await this.confirmDialog.showConfirm({
                title: 'Usunąć wpis?',
                message: 'Ta operacja jest nieodwracalna. Ten dodatek dla modelu zostanie usunięty.',
                confirmText: 'Tak, usuń',
                cancelText: 'Anuluj',
                danger: true
            })

            if (confirmed) {
                try {
                    await this.addonForModelStore.deleteAddonForModel(id)
                    showSuccessToast('Usunięto wpis.')
                } catch (err) {
                    showErrorToast(err, 'Błąd przy usuwaniu wpisu.')
                }
            }
        },
        async handleSave(id, data) {
            try {
                if (id) {
                    await this.addonForModelStore.updateAddonForModel(id, data)
                    showSuccessToast('Zaktualizowano dodatek.')
                } else {
                    await this.addonForModelStore.storeAddonForModel(data)
                    showSuccessToast('Dodano nowy dodatek.')
                }
                this.showForm = false
            } catch (err) {
                console.error('Błąd zapisu:', err)
                showErrorToast(err, 'Wystąpił błąd przy zapisie.')
            }
        },
        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page
            }
        }
    },
    created() {
        if (this.addonForModelStore.addonsForModels.length === 0) {
            this.addonForModelStore.fetchAll()
        }

        if (this.addonStore.addons.length === 0) {
            this.addonStore.fetchAddons()
        }

        if (this.trailerStore.trailers.length === 0) {
            this.trailerStore.fetchTrailers()
        }
    }
}
</script>

<style scoped lang="scss">
.addon-section {
    max-width: 1200px;
    margin: 0 auto;
    cursor: default;
}

.section-header {
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    @media (max-width: 640px) {
        align-items: flex-start;
        flex-direction: column;
    }
}

.section-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: $color-primary-dark;
    margin: 0;
}

.status-wrapper {
    margin-bottom: 1.5rem;
}

.status-loading {
    font-size: 1rem;
    color: $color-text-light;
    text-align: center;
}

.addon-list {
    display: grid;
    gap: 1.25rem;
}

.addon-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @media (min-width: 768px) {
        flex-direction: row;
        align-items: flex-start;
    }
}

.addon-image {
    width: 200px;
    height: 150px;
    object-fit: contain;
    border-radius: 6px;
    border: 1px solid $color-grey;
    flex-shrink: 0;
    background-color: $color-background;
}

.addon-details {
    display: grid;
    gap: 0.5rem;
    flex: 1;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.95rem;
}

.detail-label {
    font-weight: 600;
    color: $color-text;
    min-width: 100px;
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    svg {
        flex-shrink: 0;
    }

    &-edit {
        background: $color-primary;
        color: white;

        &:hover {
            background: $color-primary-dark;
        }
    }

    &-delete {
        background: $color-grey-light;
        color: $color-primary-accent;

        &:hover {
            background: $color-primary-accent;
            color: white;
        }
    }

    &-submit {
        background: $color-primary;
        color: white;

        &:hover {
            background: $color-primary-dark;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 3rem 2rem;
    text-align: center;
    background: $color-white;
    border-radius: 8px;
    border: 1px solid $color-grey;

    svg {
        color: $color-grey-dark;
    }

    p {
        color: $color-text-light;
        font-size: 1.1rem;
        margin: 0;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
